/**
 * Teams Meeting Bot Connection Wizard
 *
 * 5-step wizard for configuring a Microsoft 365 Teams Meeting Bot connection.
 * Lives inside the existing Connections module UI — NOT a standalone page.
 *
 * Steps:
 *   1. Platform  — choose Teams (Meet + Zoom shown as "Coming soon")
 *   2. M365 Setup — Azure AD tenant ID, app ID, bot mailbox UPN, credential
 *   3. Vexa Config — STT/TTS endpoints, auto-join policy
 *   4. Meeting Behaviour — wake words, TTS voice, context window, floor grant
 *   5. Test + Save — runs the health probe, then persists the row
 *
 * All UI primitives are from existing shadcn/ui components.
 * No custom CSS. No new dependencies.
 *
 * Spec: docs/TEAMS-VOICE-MEETING-BOT-SPEC.md §4.2 + §13 + §14
 */

import { useState } from 'react';
import { Check, X, AlertCircle, Eye, EyeOff, Loader2, Video, Mic, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { StepWizard } from '@/components/shared/step-wizard';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useCreateMeetingBotConnection,
  useTestMeetingBotConnection,
  type CreateMeetingBotConnectionInput,
  type MeetingBotTestResult,
} from '@/lib/use-connections-module';

// ── Types ──────────────────────────────────────────────────────────────────

type Platform = 'teams' | 'meet' | 'zoom';
type AuthMode = 'password' | 'oauth_refresh_token';
type AutoJoinPolicy = 'invited_only' | 'all_meetings' | 'manual_only';
type TtsMode = 'piper' | 'xtts';

// ── Props ──────────────────────────────────────────────────────────────────

interface TeamsMeetingBotWizardProps {
  onComplete: () => void;
  onCancel?: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export function TeamsMeetingBotWizard({
  onComplete,
  onCancel,
}: TeamsMeetingBotWizardProps) {
  // Step 1: Platform
  const [platform, setPlatform] = useState<Platform>('teams');

  // Step 2: M365 Setup
  const [displayName, setDisplayName] = useState('');
  const [azureAdTenantId, setAzureAdTenantId] = useState('');
  const [appId, setAppId] = useState('');
  const [botMailboxUpn, setBotMailboxUpn] = useState('');
  const [authMode, setAuthMode] = useState<AuthMode>('password');
  const [credential, setCredential] = useState('');
  const [showCredential, setShowCredential] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [botDisplayName, setBotDisplayName] = useState('Argus');

  // Step 3: Vexa Config
  const [vexaInstanceUrl, setVexaInstanceUrl] = useState('http://argusai-vexa-bot:8090');
  const [sttEndpoint, setSttEndpoint] = useState('http://argusai-stt:50051');
  const [ttsEndpoint, setTtsEndpoint] = useState('http://argusai-tts:50052');
  const [autoJoinPolicy, setAutoJoinPolicy] = useState<AutoJoinPolicy>('manual_only');
  const [maxConcurrentMeetings, setMaxConcurrentMeetings] = useState(3);

  // Step 4: Meeting Behaviour
  const [primaryWakeWords, setPrimaryWakeWords] = useState('Hey Argus');
  const [secondaryWakeWords, setSecondaryWakeWords] = useState(
    'okay Argus, go ahead Argus, Argus you can speak',
  );
  const [confidenceThreshold, setConfidenceThreshold] = useState('0.6');
  const [ttsMode, setTtsMode] = useState<TtsMode>('piper');
  const [piperVoice, setPiperVoice] = useState('en_US-amy-medium');
  const [transcriptMinutes, setTranscriptMinutes] = useState('30');
  const [topicRefreshSeconds, setTopicRefreshSeconds] = useState('30');
  const [prewarmRefreshSeconds, setPrewarmRefreshSeconds] = useState('60');
  const [requireFloorGrant, setRequireFloorGrant] = useState(true);

  // Step 5: Test + Save
  const [savedId, setSavedId] = useState<string | null>(null);
  const [testState, setTestState] = useState<'idle' | 'testing' | 'passed' | 'failed'>('idle');
  const [testResult, setTestResult] = useState<MeetingBotTestResult | null>(null);

  const createMutation = useCreateMeetingBotConnection();
  const testMutation = useTestMeetingBotConnection();

  // ── Helpers ─────────────────────────────────────────────────────────────

  const parseWakeWords = (raw: string): string[] =>
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const buildPayload = (): CreateMeetingBotConnectionInput => ({
    displayName: displayName.trim() || `${botDisplayName} Meeting Bot`,
    platform,
    vexaInstanceUrl,
    m365Config: {
      azureAdTenantId: azureAdTenantId.trim(),
      appId: appId.trim() || null,
      botMailboxUpn: botMailboxUpn.trim(),
      webhookUrl: webhookUrl.trim() || null,
      credential,
      authMode,
      graphPermissionsConsented: ['Chat.ReadWrite', 'OnlineMeetings.Read.All', 'User.Read.All'],
      botDisplayName: botDisplayName.trim() || null,
    },
    vexaConfig: {
      vexaInstanceUrl,
      maxConcurrentMeetingsForTenant: maxConcurrentMeetings,
      sttEndpoint,
      ttsEndpoint,
      autoJoinPolicy,
    },
    meetingConfig: {
      wakeWord: {
        primary: parseWakeWords(primaryWakeWords),
        secondary: parseWakeWords(secondaryWakeWords),
        confidenceThreshold: Number(confidenceThreshold) || 0.6,
      },
      tts: {
        mode: ttsMode,
        piperVoice: ttsMode === 'piper' ? piperVoice : null,
      },
      contextWindow: {
        transcriptMinutesRetained: Number(transcriptMinutes) || 30,
        topicRefreshSeconds: Number(topicRefreshSeconds) || 30,
        prewarmRefreshSeconds: Number(prewarmRefreshSeconds) || 60,
      },
      maxConcurrentMeetings,
      requireExplicitFloorGrant: requireFloorGrant,
    },
  });

  const handleSave = async () => {
    const payload = buildPayload();
    try {
      const conn = await createMutation.mutateAsync(payload);
      setSavedId(conn.id);
      toast.success('Connection saved. Running health probe...');
      // Run the test immediately after save.
      try {
        const result = await testMutation.mutateAsync(conn.id);
        setTestResult(result);
        setTestState(result.ok ? 'passed' : 'failed');
        if (result.ok) {
          toast.success('Connection verified successfully.');
        } else {
          toast.warning('Saved but health probe found issues — review the results below.');
        }
      } catch {
        setTestState('failed');
        toast.error('Health probe failed. Connection was saved; re-run test from the detail page.');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save connection.';
      toast.error(msg);
    }
  };

  const handleRunTest = async () => {
    if (!savedId) return;
    setTestState('testing');
    try {
      const result = await testMutation.mutateAsync(savedId);
      setTestResult(result);
      setTestState(result.ok ? 'passed' : 'failed');
    } catch {
      setTestState('failed');
    }
  };

  const handleComplete = () => {
    if (!savedId) {
      // User hit "Save" without the test completing — save first.
      void handleSave().then(() => onComplete());
    } else {
      onComplete();
    }
  };

  // ── Steps ────────────────────────────────────────────────────────────────

  const steps = [
    // ── Step 1: Platform ──────────────────────────────────────────────────
    {
      id: 'platform',
      title: 'Platform',
      description:
        'Choose the meeting platform for this bot. v1 ships Microsoft Teams; Google Meet and Zoom are planned.',
      canProceed: () => platform === 'teams',
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {(
              [
                {
                  id: 'teams',
                  label: 'Microsoft Teams',
                  available: true,
                  icon: '🟦',
                },
                { id: 'meet', label: 'Google Meet', available: false, icon: '🟩' },
                { id: 'zoom', label: 'Zoom', available: false, icon: '🟦' },
              ] as Array<{ id: Platform; label: string; available: boolean; icon: string }>
            ).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => p.available && setPlatform(p.id)}
                aria-disabled={!p.available}
                title={p.available ? `Select ${p.label}` : `${p.label} — coming soon`}
                className={cn(
                  'relative flex flex-col items-center gap-2 rounded-lg border p-4 transition-colors text-center',
                  p.available
                    ? 'cursor-pointer hover:border-primary/60'
                    : 'cursor-not-allowed opacity-50',
                  platform === p.id && p.available
                    ? 'border-primary bg-primary/5'
                    : 'border-border',
                )}
              >
                <span className="text-2xl">{p.icon}</span>
                <span className="text-sm font-medium">{p.label}</span>
                {!p.available && (
                  <Badge variant="secondary" className="text-[9px] mt-1">
                    Coming soon
                  </Badge>
                )}
                {platform === p.id && p.available && (
                  <Check className="absolute top-2 right-2 h-3.5 w-3.5 text-primary" />
                )}
              </button>
            ))}
          </div>
          {platform === 'teams' && (
            <Card className="bg-muted/30 border-muted">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">
                  <strong>What you will need (one-time, ~30–60 min):</strong>
                </p>
                <ol className="text-xs text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                  <li>A dedicated M365 mailbox for the bot (e.g. argus@your-domain.com)</li>
                  <li>
                    The Azure AD tenant ID and, optionally, an app registration client ID
                  </li>
                  <li>
                    Admin consent for three Graph scopes:{' '}
                    <code className="text-[10px] bg-muted rounded px-0.5">
                      Chat.ReadWrite
                    </code>{' '}
                    <code className="text-[10px] bg-muted rounded px-0.5">
                      OnlineMeetings.Read.All
                    </code>{' '}
                    <code className="text-[10px] bg-muted rounded px-0.5">
                      User.Read.All
                    </code>
                  </li>
                </ol>
                <p className="text-xs text-muted-foreground mt-2">
                  No Azure Bot Service, no Partner Center registration, no AppSource
                  submission required. The bot joins as a regular Teams user via Vexa.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      ),
    },

    // ── Step 2: M365 Setup ────────────────────────────────────────────────
    {
      id: 'm365',
      title: 'M365 Setup',
      description:
        'Configure the bot mailbox identity and credentials. The password or refresh token is encrypted at rest.',
      canProceed: () =>
        azureAdTenantId.trim().length > 0 &&
        botMailboxUpn.trim().length > 0 &&
        botMailboxUpn.includes('@') &&
        credential.length > 0,
      content: (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="mb-displayName">Connection display name</Label>
            <Input
              id="mb-displayName"
              placeholder="e.g. Argus Teams Bot (Production)"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Shown in the Connections list. Defaults to "{botDisplayName} Meeting Bot".
            </p>
          </div>

          <Separator />

          <div className="space-y-1.5">
            <Label htmlFor="mb-tenantId">Azure AD Tenant ID *</Label>
            <Input
              id="mb-tenantId"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={azureAdTenantId}
              onChange={(e) => setAzureAdTenantId(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Found in Azure Portal → Azure Active Directory → Overview → Tenant ID.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-appId">
              App registration client ID{' '}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="mb-appId"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Required for OAuth refresh-token mode or if your tenant has conditional access.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-upn">Bot mailbox UPN *</Label>
            <Input
              id="mb-upn"
              type="email"
              placeholder="argus@your-domain.onmicrosoft.com"
              value={botMailboxUpn}
              onChange={(e) => setBotMailboxUpn(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-botDisplayName">Bot display name in meetings</Label>
            <Input
              id="mb-botDisplayName"
              placeholder="Argus"
              value={botDisplayName}
              onChange={(e) => setBotDisplayName(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-authMode">Auth mode *</Label>
            <Select
              value={authMode}
              onValueChange={(v) => setAuthMode(v as AuthMode)}
            >
              <SelectTrigger id="mb-authMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="password">Password (simple)</SelectItem>
                <SelectItem value="oauth_refresh_token">
                  OAuth refresh token (recommended for conditional-access tenants)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-credential">
              {authMode === 'password' ? 'Bot mailbox password' : 'OAuth refresh token'} *
            </Label>
            <div className="relative">
              <Input
                id="mb-credential"
                type={showCredential ? 'text' : 'password'}
                placeholder={
                  authMode === 'password' ? 'Bot account password' : 'Refresh token'
                }
                value={credential}
                onChange={(e) => setCredential(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCredential((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                title={showCredential ? 'Hide' : 'Show'}
                aria-label={showCredential ? 'Hide credential' : 'Show credential'}
              >
                {showCredential ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Encrypted at rest with AES-256-GCM. Never echoed back after save.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-webhook">
              Graph webhook URL{' '}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="mb-webhook"
              placeholder="https://your-domain.com/graph-webhooks"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
            />
          </div>
        </div>
      ),
    },

    // ── Step 3: Vexa Config ───────────────────────────────────────────────
    {
      id: 'vexa',
      title: 'Vexa Config',
      description:
        'Configure the Vexa container endpoints and meeting join policy. Defaults work for standard ArgusAI deployments.',
      canProceed: () =>
        vexaInstanceUrl.trim().length > 0 &&
        sttEndpoint.trim().length > 0 &&
        ttsEndpoint.trim().length > 0,
      content: (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="mb-vexaUrl">
              Vexa container URL
              <Mic className="inline h-3.5 w-3.5 ml-1.5 text-muted-foreground" />
            </Label>
            <Input
              id="mb-vexaUrl"
              placeholder="http://argusai-vexa-bot:8090"
              value={vexaInstanceUrl}
              onChange={(e) => setVexaInstanceUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              The shared <code className="text-[10px] bg-muted rounded px-0.5">argusai-vexa-bot</code> container on the internal Docker network.
              Change only if Vexa runs on a custom port.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-sttEndpoint">
              STT (Whisper) endpoint
              <Mic className="inline h-3.5 w-3.5 ml-1.5 text-muted-foreground" />
            </Label>
            <Input
              id="mb-sttEndpoint"
              placeholder="http://argusai-stt:50051"
              value={sttEndpoint}
              onChange={(e) => setSttEndpoint(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              gRPC endpoint for the <code className="text-[10px] bg-muted rounded px-0.5">argusai-stt</code> Whisper streaming sidecar.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-ttsEndpoint">
              TTS endpoint
              <Volume2 className="inline h-3.5 w-3.5 ml-1.5 text-muted-foreground" />
            </Label>
            <Input
              id="mb-ttsEndpoint"
              placeholder="http://argusai-tts:50052"
              value={ttsEndpoint}
              onChange={(e) => setTtsEndpoint(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              gRPC endpoint for the <code className="text-[10px] bg-muted rounded px-0.5">argusai-tts</code> Piper TTS sidecar.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-autoJoin">Auto-join policy</Label>
            <Select
              value={autoJoinPolicy}
              onValueChange={(v) => setAutoJoinPolicy(v as AutoJoinPolicy)}
            >
              <SelectTrigger id="mb-autoJoin">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual_only">
                  Manual only — admin triggers join for each meeting
                </SelectItem>
                <SelectItem value="invited_only">
                  Invited only — bot joins when explicitly invited
                </SelectItem>
                <SelectItem value="all_meetings">
                  All meetings — bot joins all meetings organised by the bot mailbox
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-maxMeetings">
              Max concurrent meetings for this tenant (1–20)
            </Label>
            <Input
              id="mb-maxMeetings"
              type="number"
              min={1}
              max={20}
              value={maxConcurrentMeetings}
              onChange={(e) =>
                setMaxConcurrentMeetings(Math.max(1, Math.min(20, Number(e.target.value))))
              }
            />
            <p className="text-xs text-muted-foreground">
              Global ceiling is set by <code className="text-[10px] bg-muted rounded px-0.5">VEXA_MAX_CONCURRENT_MEETINGS</code> on the Vexa container.
            </p>
          </div>
        </div>
      ),
    },

    // ── Step 4: Meeting Behaviour ─────────────────────────────────────────
    {
      id: 'behaviour',
      title: 'Meeting Behaviour',
      description:
        'Configure wake words, TTS voice, and context window settings.',
      canProceed: () =>
        parseWakeWords(primaryWakeWords).length > 0,
      content: (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="mb-primaryWake">
              Primary wake words <span className="text-destructive">*</span>
            </Label>
            <Input
              id="mb-primaryWake"
              placeholder="Hey Argus, Argus"
              value={primaryWakeWords}
              onChange={(e) => setPrimaryWakeWords(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated. Detected by openWakeWord. "Hey Argus" is recommended to
              reduce false positives.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-secondaryWake">Secondary (floor-grant) wake words</Label>
            <Input
              id="mb-secondaryWake"
              placeholder="okay Argus, go ahead Argus"
              value={secondaryWakeWords}
              onChange={(e) => setSecondaryWakeWords(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Detected after the bot raises its hand. Also triggered by
              "@Argus go ahead" in the meeting chat.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mb-confidence">Wake-word confidence threshold (0.0–1.0)</Label>
            <Input
              id="mb-confidence"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={confidenceThreshold}
              onChange={(e) => setConfidenceThreshold(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              0.6 is the recommended starting point. Lower = more sensitive (more false
              positives). Higher = less sensitive (may miss quiet voices).
            </p>
          </div>

          <Separator />

          <div className="space-y-1.5">
            <Label htmlFor="mb-ttsMode">TTS mode</Label>
            <Select value={ttsMode} onValueChange={(v) => setTtsMode(v as TtsMode)}>
              <SelectTrigger id="mb-ttsMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="piper">Piper (Phase 1 — CPU, fast, clear)</SelectItem>
                <SelectItem value="xtts" disabled aria-disabled="true">
                  XTTS v2 — cloned voice (Phase 2, requires consent record)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {ttsMode === 'piper' && (
            <div className="space-y-1.5">
              <Label htmlFor="mb-piperVoice">Piper voice</Label>
              <Select value={piperVoice} onValueChange={setPiperVoice}>
                <SelectTrigger id="mb-piperVoice">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en_US-amy-medium">
                    en_US-amy-medium (neutral female, default)
                  </SelectItem>
                  <SelectItem value="en_US-ryan-high">
                    en_US-ryan-high (neutral male)
                  </SelectItem>
                  <SelectItem value="en_US-libritts-high">
                    en_US-libritts-high (wider voice pool)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <Separator />

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="mb-transcriptMin">Transcript window (minutes)</Label>
              <Input
                id="mb-transcriptMin"
                type="number"
                min={5}
                max={480}
                value={transcriptMinutes}
                onChange={(e) => setTranscriptMinutes(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mb-topicRefresh">Topic refresh (seconds)</Label>
              <Input
                id="mb-topicRefresh"
                type="number"
                min={10}
                max={300}
                value={topicRefreshSeconds}
                onChange={(e) => setTopicRefreshSeconds(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mb-prewarm">Pre-warm interval (seconds)</Label>
              <Input
                id="mb-prewarm"
                type="number"
                min={10}
                max={600}
                value={prewarmRefreshSeconds}
                onChange={(e) => setPrewarmRefreshSeconds(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <div>
              <p className="text-sm font-medium">Require explicit floor grant</p>
              <p className="text-xs text-muted-foreground">
                If on, bot waits for secondary wake word before speaking. Recommended.
              </p>
            </div>
            <Switch
              checked={requireFloorGrant}
              onCheckedChange={setRequireFloorGrant}
              aria-label="Require explicit floor grant"
            />
          </div>
        </div>
      ),
    },

    // ── Step 5: Test + Save ───────────────────────────────────────────────
    {
      id: 'test-save',
      title: 'Test + Save',
      description:
        'Save the connection and run a health probe against the Vexa container and M365 Graph endpoint.',
      canProceed: () => savedId !== null,
      content: (
        <div className="space-y-4">
          {/* Summary card */}
          <Card className="bg-muted/30">
            <CardContent className="pt-4 pb-3 space-y-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-muted-foreground">Platform</span>
                <span className="font-medium capitalize">{platform}</span>
                <span className="text-muted-foreground">Bot UPN</span>
                <span className="font-mono text-xs truncate">{botMailboxUpn}</span>
                <span className="text-muted-foreground">Auth mode</span>
                <span>
                  {authMode === 'password' ? 'Password' : 'OAuth refresh token'}
                </span>
                <span className="text-muted-foreground">Vexa URL</span>
                <span className="font-mono text-xs truncate">{vexaInstanceUrl}</span>
                <span className="text-muted-foreground">Wake words</span>
                <span className="text-xs truncate">{primaryWakeWords}</span>
              </div>
            </CardContent>
          </Card>

          {/* Save button (appears before save; hidden after) */}
          {!savedId && (
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || testMutation.isPending}
              className="w-full gap-2"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Video className="h-4 w-4" /> Save and Test Connection
                </>
              )}
            </Button>
          )}

          {/* Test result panel */}
          {testState === 'testing' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Running health probe…
            </div>
          )}

          {testResult && (testState === 'passed' || testState === 'failed') && (
            <div className="space-y-2">
              <div
                className={cn(
                  'flex items-center gap-2 text-sm font-medium',
                  testResult.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive',
                )}
              >
                {testResult.ok ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                {testResult.message}
                {testResult.latencyMs != null && (
                  <span className="text-xs text-muted-foreground font-normal">
                    ({testResult.latencyMs}ms)
                  </span>
                )}
              </div>

              {testResult.steps && testResult.steps.length > 0 && (
                <div className="space-y-1 ml-6">
                  {testResult.steps.map((step) => (
                    <div
                      key={step.name}
                      className="flex items-start gap-2 text-xs"
                    >
                      {step.ok ? (
                        <Check className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
                      ) : (
                        <X className="h-3 w-3 text-destructive mt-0.5 shrink-0" />
                      )}
                      <span>
                        <span className="font-medium">
                          {step.name.replace(/_/g, ' ')}
                        </span>
                        {' — '}
                        {step.message}
                        {step.latencyMs != null && (
                          <span className="text-muted-foreground/70">
                            {' '}({step.latencyMs}ms)
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {savedId && testState === 'failed' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRunTest}
                  disabled={testMutation.isPending}
                  className="gap-1.5"
                >
                  {testMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Video className="h-3.5 w-3.5" />
                  )}{' '}
                  Re-run test
                </Button>
              )}
            </div>
          )}

          {savedId && testState === 'passed' && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400">
              Connection saved and verified. Click <strong>Done</strong> to finish.
            </p>
          )}

          {savedId && testState === 'failed' && (
            <p className="text-xs text-muted-foreground">
              The connection was saved but the health probe failed. You can complete the
              wizard now and fix the issue from the connection detail page, or re-run the
              test above after correcting the configuration.
            </p>
          )}
        </div>
      ),
    },
  ];

  return (
    <StepWizard
      steps={steps}
      onComplete={handleComplete}
      onCancel={onCancel}
      completeLabel="Done"
    />
  );
}
